/* ChainRibbon.jsx — calm operational chain intelligence ribbon.
 *
 * Horizontal, scannable, premium. Each linked transaction is a
 * lightweight pill carrying address · stage · readiness state ·
 * top blocker. Hairline connectors between pills suggest the
 * directional buyer→seller flow. This is NOT a chain map. It is
 * NOT a graph. It is an airline-style coordination strip.
 *
 * Doctrine:
 *   - Premium, restrained, calm.
 *   - Reuses `exchangeReadiness.js` — no second logic system.
 *   - Operator vocabulary only.
 *   - Mobile: horizontal scroll-snap, thumb-friendly.
 *   - Never overpowers the page.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ChevronRight, AlertCircle } from "lucide-react";
import { resolveChain, chainSignal } from "../../lib/chainRelationships";
import { READINESS_STATE_TONE } from "../../lib/exchangeReadiness";
import {
  computeSellerConfidence,
  SELLER_CONFIDENCE_TONE,
} from "../../lib/operationalSignals";

const SIGNAL_TONE = {
  calm:  { dot: "#10B981", fg: "#047857", bg: "rgba(16,185,129,0.10)" },
  amber: { dot: "#F59E0B", fg: "#B45309", bg: "rgba(245,158,11,0.10)" },
  warn:  { dot: "#EF4444", fg: "#B91C1C", bg: "rgba(239,68,68,0.10)" },
};

// Short address line — first line of the deal name / property.
function _shortAddress(deal) {
  if (!deal) return "—";
  return (deal.name || deal.address || "Untitled deal").split(",")[0].trim().slice(0, 32);
}

// Days-to-exchange — re-derived inline so the ribbon never
// requires a separate fetch.
function _daysToExchange(deal) {
  if (!deal?.targetExchange) return null;
  const t = new Date(deal.targetExchange).getTime();
  if (Number.isNaN(t)) return null;
  return Math.round((t - Date.now()) / 86_400_000);
}

function ChainNode({ peer, index, isLast }) {
  const { deal, isThis, readiness, stageBucket } = peer;
  const tone = READINESS_STATE_TONE[readiness.state] || READINESS_STATE_TONE["Progressing Well"];
  // Embedded seller-confidence dot per node — derived deterministically
  // from the deal's activity feed (when present in the row) and stage
  // metadata. Tiny, never alarming, never re-renders.
  const sellerSig = useMemo(
    () => computeSellerConfidence(deal, deal?.activity || []),
    [deal]
  );
  const sellerTone = SELLER_CONFIDENCE_TONE[sellerSig.state];
  const days = _daysToExchange(deal);
  const daysLabel = days == null
    ? "—"
    : days < 0 ? `${Math.abs(days)}d overdue`
    : days === 0 ? "today"
    : `${days}d`;

  return (
    <>
      <Link
        to={`/deal-rooms/${deal.id}`}
        className={[
          "shrink-0 group relative",
          "snap-start",
          "rounded-xl bg-white",
          "px-3.5 py-3 sm:py-2.5",
          "min-w-[200px] sm:min-w-[220px] max-w-[260px]",
          "cf-card-hover transition-colors",
          isThis ? "ring-1 ring-violet-200" : "border border-slate-100",
        ].join(" ")}
        style={{
          boxShadow: isThis
            ? "0 1px 2px rgba(99,102,241,0.06)"
            : "0 1px 2px rgba(15,23,42,0.02)",
          borderColor: isThis ? "rgba(139,92,246,0.32)" : undefined,
          borderWidth: isThis ? "1px" : undefined,
          borderStyle: isThis ? "solid" : undefined,
        }}
        data-testid={`chain-ribbon-node-${index}`}
        data-is-this={isThis ? "true" : "false"}
      >
        {/* Header row: position chip · stage · days */}
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">
            {peer.position === "bottom" ? (
              <ArrowDown size={9} strokeWidth={2.5} className="text-slate-400" />
            ) : peer.position === "top" ? (
              <ArrowUp size={9} strokeWidth={2.5} className="text-slate-400" />
            ) : null}
            {isThis ? "This deal" : peer.position === "bottom" ? "Buyer side" : peer.position === "top" ? "Seller side" : "Chain"}
          </span>
          <span className="text-[10.5px] tabular-nums text-slate-400 shrink-0">{daysLabel}</span>
        </div>

        {/* Address */}
        <p className="text-[12.5px] font-semibold text-slate-900 leading-tight truncate">
          {_shortAddress(deal)}
        </p>

        {/* Stage + readiness state row */}
        <div className="mt-2 flex items-center gap-2 min-w-0">
          <span className="text-[11px] text-slate-500 shrink-0">{stageBucket}</span>
          <span className="w-1 h-1 rounded-full bg-slate-300 shrink-0" aria-hidden />
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold truncate"
            style={{ color: tone.fg }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: tone.dot }}
              aria-hidden
            />
            <span className="truncate">{readiness.state}</span>
          </span>
        </div>

        {/* Top blocker — single line, calm */}
        {readiness.topBlocker && readiness.state !== "Exchanged" && (
          <p
            className="mt-1.5 text-[11px] text-slate-500 truncate"
            data-testid={`chain-ribbon-blocker-${index}`}
          >
            {readiness.topBlocker}
          </p>
        )}

        {/* Seller-confidence sliver — one tiny pill, only when not Stable.
            We deliberately suppress the pill on calm nodes so the strip
            stays calm; it surfaces only when something needs eyes. */}
        {sellerSig.state !== "Stable" && sellerTone && (
          <span
            className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold whitespace-nowrap"
            style={{
              color: sellerTone.fg,
              background: sellerTone.bg,
              border: `1px solid ${sellerTone.ring || sellerTone.bg}`,
              padding: "1px 7px 1px 6px",
              borderRadius: 999,
              lineHeight: 1.4,
            }}
            data-testid={`chain-ribbon-seller-confidence-${index}`}
            data-state={sellerSig.state}
            title={sellerSig.reasons?.[0] ? `${sellerSig.state} · ${sellerSig.reasons[0]}` : sellerSig.state}
          >
            <span
              aria-hidden
              className="w-1 h-1 rounded-full shrink-0"
              style={{ background: sellerTone.dot }}
            />
            {sellerSig.state}
          </span>
        )}
      </Link>

      {/* Connector hairline between nodes */}
      {!isLast && (
        <span
          aria-hidden
          className="shrink-0 self-center h-px w-5 sm:w-7 bg-slate-200 mx-0"
        />
      )}
    </>
  );
}

export function ChainRibbon({
  deal,
  allDeals,
  testid = "chain-ribbon",
  title = "Chain",
  density = "comfortable", // comfortable | compact
}) {
  const { peers, weakestLink } = useMemo(
    () => resolveChain(deal, allDeals || []),
    [deal, allDeals]
  );
  const signal = useMemo(() => chainSignal(peers), [peers]);
  const signalTone = SIGNAL_TONE[signal.tone];

  if (!peers || peers.length === 0) return null;

  const weakAddress = weakestLink && !weakestLink.isThis
    ? _shortAddress(weakestLink.deal)
    : null;

  return (
    <section
      className={`rounded-2xl bg-white border border-slate-100 ${density === "compact" ? "px-4 py-3" : "px-4 sm:px-5 py-4"}`}
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      data-testid={testid}
      aria-label="Persistent chain ribbon"
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-500">
            {title}
          </p>
          <p className="text-[12px] text-slate-500 mt-0.5 truncate">
            {peers.length} linked transaction{peers.length === 1 ? "" : "s"}
            {weakAddress && signal.tone !== "calm" && (
              <>
                <span className="text-slate-300 mx-1.5">·</span>
                <span className="text-slate-600">
                  Slowest · {weakAddress}
                </span>
              </>
            )}
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-2 h-6 rounded-full text-[11px] font-semibold shrink-0"
          style={{ background: signalTone.bg, color: signalTone.fg }}
          data-testid={`${testid}-signal`}
        >
          {signal.tone === "warn" && <AlertCircle size={10} strokeWidth={2.5} />}
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: signalTone.dot }}
            aria-hidden
          />
          {signal.label}
        </span>
      </div>

      {/* The ribbon — horizontal, scroll-snap, scannable */}
      <ol
        className="flex items-stretch gap-0 overflow-x-auto cf-scroll snap-x snap-mandatory pb-1 -mx-1 px-1"
        data-testid={`${testid}-track`}
        style={{ scrollPaddingInline: "8px" }}
      >
        {peers.map((p, i) => (
          <ChainNode
            key={p.deal.id}
            peer={p}
            index={i}
            isLast={i === peers.length - 1}
          />
        ))}
      </ol>
    </section>
  );
}

/**
 * Compact one-line ChainRibbon variant for table contexts
 * (Deals page row toolbar, Mission Control header strip).
 * Shows signal pill + N linked + ChevronRight to deep dive.
 */
export function ChainRibbonInline({ deal, allDeals, onOpen, testid = "chain-ribbon-inline" }) {
  const { peers } = useMemo(
    () => resolveChain(deal, allDeals || []),
    [deal, allDeals]
  );
  const signal = useMemo(() => chainSignal(peers), [peers]);
  if (!peers || peers.length === 0) return null;
  const tone = SIGNAL_TONE[signal.tone];
  return (
    <button
      type="button"
      onClick={onOpen}
      className="inline-flex items-center gap-2 h-7 px-2.5 rounded-md bg-white border border-slate-100 hover:border-slate-200 text-[11.5px] text-slate-700 transition-colors cf-press"
      data-testid={testid}
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tone.dot }} aria-hidden />
      <span className="font-semibold">{peers.length}-deal chain</span>
      <span className="text-slate-400" style={{ color: tone.fg }}>· {signal.label}</span>
      <ChevronRight size={10} strokeWidth={2.5} className="text-slate-400" />
    </button>
  );
}
